import Phaser from "phaser";
import { trackUiClick, type AnalyticsSource } from "../analytics.js";
import type { PasswordPromptConfig } from "../shell/denied-commands.js";
import type { VfsSession } from "../shell/vfs.js";
import {
    COLORS,
    FONT,
    FONT_SIZE,
    FOOTER_DEPTH,
    INPUT_GRADIENT_HEIGHT,
    INPUT_GRADIENT_STEPS,
    INPUT_PIXEL,
    LINE_H,
    PADDING,
    STATUS_FONT_SIZE,
    STATUS_ITEM_GAP,
    STATUS_LINE_H,
    STATUS_GAP,
} from "./constants.js";

export interface StatusBarConfig {
    menu: Array<{ label: string; command: string }>;
    label: string;
    value: string;
}

export interface TerminalInputCallbacks {
    playClick: () => void;
    runCommand: (command: string, source: AnalyticsSource) => void;
    getShellPrompt: () => string;
}

export class TerminalInputChrome {
    promptText!: Phaser.GameObjects.Text;
    inputText!: Phaser.GameObjects.Text;
    cursor!: Phaser.GameObjects.Text;
    readonly backdrop: Phaser.GameObjects.Graphics;

    private statusBarItems: Phaser.GameObjects.Text[] = [];
    private statusBarLabel!: Phaser.GameObjects.Text;
    private statusBarValue!: Phaser.GameObjects.Text;
    private cachedShellPrompt = "";
    private cachedInputDisplay = "";

    constructor(
        private readonly scene: Phaser.Scene,
        private readonly callbacks: TerminalInputCallbacks,
    ) {
        this.backdrop = this.scene.add.graphics();
        this.backdrop.setScrollFactor(0, 0);
        this.backdrop.setDepth(FOOTER_DEPTH);
    }

    createPrompt(): void {
        this.promptText = this.scene.add
            .text(0, 0, this.callbacks.getShellPrompt(), {
                fontFamily: FONT,
                fontSize: FONT_SIZE,
                color: COLORS.amber,
            })
            .setScrollFactor(0, 0)
            .setDepth(FOOTER_DEPTH);

        this.inputText = this.scene.add
            .text(0, 0, "", {
                fontFamily: FONT,
                fontSize: FONT_SIZE,
                color: COLORS.text,
            })
            .setScrollFactor(0, 0)
            .setDepth(FOOTER_DEPTH);

        this.cursor = this.scene.add
            .text(0, 0, "█", {
                fontFamily: FONT,
                fontSize: FONT_SIZE,
                color: COLORS.text,
            })
            .setScrollFactor(0, 0)
            .setDepth(FOOTER_DEPTH);
        this.cursor.setVisible(false);

        this.scene.tweens.add({
            targets: this.cursor,
            alpha: 0,
            duration: 500,
            yoyo: true,
            repeat: -1,
        });
    }

    createStatusBar(statusBar: StatusBarConfig): void {
        for (const entry of statusBar.menu) {
            this.statusBarItems.push(this.createFixedClickableText(entry.label, entry.command));
        }

        this.statusBarLabel = this.scene.add
            .text(0, 0, statusBar.label, {
                fontFamily: FONT,
                fontSize: STATUS_FONT_SIZE,
                color: COLORS.dim,
            })
            .setScrollFactor(0, 0)
            .setDepth(FOOTER_DEPTH);

        this.statusBarValue = this.scene.add
            .text(0, 0, statusBar.value, {
                fontFamily: FONT,
                fontSize: STATUS_FONT_SIZE,
                color: COLORS.bright,
            })
            .setScrollFactor(0, 0)
            .setDepth(FOOTER_DEPTH);

        this.scene.tweens.add({
            targets: this.statusBarValue,
            alpha: 0.45,
            duration: 900,
            yoyo: true,
            repeat: -1,
            ease: "Sine.easeInOut",
        });
    }

    setVisible(visible: boolean): void {
        this.promptText.setVisible(visible);
        this.inputText.setVisible(visible);
        this.cursor.setVisible(visible);
        this.backdrop.setVisible(visible);
        for (const item of this.statusBarItems) {
            item.setVisible(visible);
        }
        this.statusBarLabel?.setVisible(visible);
        this.statusBarValue?.setVisible(visible);
    }

    statusBarTop(scaleHeight: number): number {
        return scaleHeight - PADDING - STATUS_LINE_H;
    }

    promptTop(scaleHeight: number, inputEnabled: boolean): number {
        if (!inputEnabled) {
            return scaleHeight - PADDING;
        }
        return this.statusBarTop(scaleHeight) - STATUS_GAP - LINE_H;
    }

    drawBackdrop(scaleWidth: number, scaleHeight: number, solidTop: number): void {
        const width = scaleWidth;
        const height = scaleHeight;
        const gradientTop = Math.max(0, solidTop - INPUT_GRADIENT_HEIGHT);
        const rows = Math.ceil((solidTop - gradientTop) / INPUT_PIXEL);

        this.backdrop.clear();
        this.backdrop.fillStyle(COLORS.bg, 1);
        this.backdrop.fillRect(0, solidTop, width, height - solidTop);

        for (let row = 0; row < rows; row += 1) {
            const y = gradientTop + row * INPUT_PIXEL;
            const rowHeight = Math.min(INPUT_PIXEL, solidTop - y);
            if (rowHeight <= 0) continue;

            const progress = rows <= 1 ? 1 : row / (rows - 1);
            const level = Math.floor(progress * INPUT_GRADIENT_STEPS);
            if (level <= 0) continue;

            for (let x = 0; x < width; x += INPUT_PIXEL) {
                const blockW = Math.min(INPUT_PIXEL, width - x);
                const ditherOn =
                    level >= INPUT_GRADIENT_STEPS ||
                    (x / INPUT_PIXEL + row) % 2 === 0
                        ? level
                        : Math.max(0, level - 1);
                const blockAlpha = ditherOn / INPUT_GRADIENT_STEPS;
                if (blockAlpha <= 0) continue;

                this.backdrop.fillStyle(COLORS.bg, blockAlpha);
                this.backdrop.fillRect(x, y, blockW, rowHeight);
            }
        }
    }

    layoutStatusBar(scaleWidth: number, scaleHeight: number): void {
        const y = this.statusBarTop(scaleHeight);
        let x = PADDING;

        for (const item of this.statusBarItems) {
            item.setPosition(x, y);
            x += item.width + STATUS_ITEM_GAP;
        }

        const statusWidth = this.statusBarLabel.width + this.statusBarValue.width;
        const statusX = scaleWidth - PADDING - statusWidth;

        this.statusBarLabel.setPosition(statusX, y);
        this.statusBarValue.setPosition(statusX + this.statusBarLabel.width, y);
    }

    updatePrompt(
        promptTop: number,
        commandBuffer: string,
        passwordConfig: PasswordPromptConfig | null,
        passwordBuffer: string,
    ): void {
        if (!passwordConfig) {
            const prompt = this.callbacks.getShellPrompt();
            if (prompt !== this.cachedShellPrompt) {
                this.cachedShellPrompt = prompt;
                this.promptText.setText(prompt);
            }
        }

        const promptWidth = this.promptText.width;
        const inputX = PADDING + promptWidth + 12;

        this.promptText.setPosition(PADDING, promptTop);
        this.inputText.setPosition(inputX, promptTop);

        const inputDisplay = passwordConfig
            ? "*".repeat(passwordBuffer.length)
            : commandBuffer;

        if (inputDisplay !== this.cachedInputDisplay) {
            this.cachedInputDisplay = inputDisplay;
            this.inputText.setText(inputDisplay);
        }

        this.cursor.setPosition(inputX + this.inputText.width + 4, promptTop);
    }

    setPasswordPrompt(prompt: string): void {
        this.cachedShellPrompt = prompt;
        this.cachedInputDisplay = "";
        this.promptText.setText(prompt);
        this.inputText.setText("");
    }

    resetPromptCache(shellPrompt: string): void {
        this.cachedShellPrompt = shellPrompt;
        this.cachedInputDisplay = "";
        this.promptText.setText(shellPrompt);
        this.inputText.setText("");
    }

    private createFixedClickableText(
        label: string,
        command: string,
    ): Phaser.GameObjects.Text {
        const item = this.scene.add
            .text(0, 0, label, {
                fontFamily: FONT,
                fontSize: STATUS_FONT_SIZE,
                color: COLORS.text,
            })
            .setScrollFactor(0, 0)
            .setDepth(FOOTER_DEPTH)
            .setInteractive({ useHandCursor: true })
            .on("pointerover", () => item.setColor(COLORS.amber))
            .on("pointerout", () => item.setColor(COLORS.text))
            .on("pointerdown", () => {
                this.callbacks.playClick();
                trackUiClick("menu", label, command);
                this.callbacks.runCommand(command, "menu");
            });

        return item;
    }
}

export function formatShellPrompt(vfs: VfsSession, handle: string, hostname: string): string {
    return vfs.formatPrompt(handle, hostname);
}
